/**
 * Prompt Builder Modal
 * Visual prompt builder for creating AI prompts with templates, layouts, styles, and animations
 */

import { useState, useMemo, useCallback } from "react"
import { X, ChevronDown, ChevronRight, Search, RotateCcw, Plus, Check, Code } from "lucide-react"
import { Button } from "@aix/ui"
import {
  PROMPT_SECTIONS,
  TEMPLATE_CATEGORIES,
  DEVICE_TYPES,
  DURATION_SLIDER,
  DELAY_SLIDER,
} from "./promptBuilderConfig"
import type { PromptOption, GeneratedPrompt } from "./promptBuilderConfig"

interface PromptBuilderModalProps {
  isOpen: boolean
  onClose: () => void
  onAddToPrompt: (prompt: string) => void
}

// Mock template data - in production this would come from API
const MOCK_TEMPLATES = [
  { id: "1", name: "AI Consultancy Landing", author: "Meng To", views: 402, isPro: true, thumbnail: "" },
  { id: "2", name: "Roam Stays Landing", author: "Sam", views: 361, isPro: true, thumbnail: "" },
  { id: "3", name: "Architecture Studio", author: "Vannarot Roe...", views: 421, isPro: true, thumbnail: "" },
  { id: "4", name: "Mira.ai Intelligence", author: "Sourasith Ph...", views: 1250, isPro: true, thumbnail: "" },
  { id: "5", name: "Web App Multi Pages", author: "Francois Sav...", views: 857, isPro: false, thumbnail: "" },
  { id: "6", name: "Car Dealership Landing", author: "Simon Kinn", views: 245, isPro: false, thumbnail: "" },
]

const PromptBuilderModal = ({ isOpen, onClose, onAddToPrompt }: PromptBuilderModalProps) => {
  // State for tabs
  const [activeTab, setActiveTab] = useState<"template" | "html">("template")

  // State for template gallery
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>("2") // Default to Roam Stays
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [deviceType, setDeviceType] = useState("web")

  // State for HTML paste
  const [pastedHtml, setPastedHtml] = useState("")

  // State for selections (keyed by section ID)
  const [selections, setSelections] = useState<Record<string, string | string[]>>({})

  // State for sliders
  const [duration, setDuration] = useState(DURATION_SLIDER.defaultValue)
  const [delay, setDelay] = useState(DELAY_SLIDER.defaultValue)

  // State for collapsed sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  // State for generated prompts that have been added
  const [addedPrompts, setAddedPrompts] = useState<Set<string>>(new Set())

  // State for custom prompt
  const [customPrompt, setCustomPrompt] = useState("")

  // Generate prompts based on selections - all selections are auto-added
  const generatedPrompts = useMemo(() => {
    const prompts: GeneratedPrompt[] = []

    // Add template-based prompt if template selected
    if (activeTab === "template" && selectedTemplate) {
      const template = MOCK_TEMPLATES.find((t) => t.id === selectedTemplate)
      if (template) {
        prompts.push({
          id: `template-${selectedTemplate}`,
          text: `Adapt from ${template.name} template (78946 chars)`,
          category: "template",
          isAdded: true, // Auto-added
        })
      }
    }

    // Add HTML-based prompt if HTML pasted
    if (activeTab === "html" && pastedHtml.trim()) {
      prompts.push({
        id: "html-paste",
        text: `Adapt from pasted HTML (${pastedHtml.length} chars)`,
        category: "html",
        isAdded: true, // Auto-added
      })
    }

    // Add prompts for each section selection - all auto-added
    PROMPT_SECTIONS.forEach((section) => {
      const selection = selections[section.id]
      if (!selection) return

      if (Array.isArray(selection)) {
        // Multi-select
        selection.forEach((optionId) => {
          const option = section.options.find((o) => o.id === optionId)
          if (option) {
            const promptId = `${section.id}-${optionId}`
            prompts.push({
              id: promptId,
              text: option.promptText,
              category: section.id,
              isAdded: true, // Auto-added
            })
          }
        })
      } else {
        // Single select
        const option = section.options.find((o) => o.id === selection)
        if (option) {
          const promptId = `${section.id}-${selection}`
          prompts.push({
            id: promptId,
            text: option.promptText,
            category: section.id,
            isAdded: true, // Auto-added
          })
        }
      }
    })

    // Add duration prompt if not default
    if (duration !== DURATION_SLIDER.defaultValue) {
      const promptId = `duration-${duration}`
      prompts.push({
        id: promptId,
        text: `${DURATION_SLIDER.promptPrefix} ${duration}${DURATION_SLIDER.unit}`,
        category: "duration",
        isAdded: true, // Auto-added
      })
    }

    // Add delay prompt if not default
    if (delay !== DELAY_SLIDER.defaultValue) {
      const promptId = `delay-${delay}`
      prompts.push({
        id: promptId,
        text: `${DELAY_SLIDER.promptPrefix} ${delay}${DELAY_SLIDER.unit}`,
        category: "delay",
        isAdded: true, // Auto-added
      })
    }

    // Add custom prompt if entered
    if (customPrompt.trim()) {
      prompts.push({
        id: "custom",
        text: customPrompt.trim(),
        category: "custom",
        isAdded: addedPrompts.has("custom"), // Custom prompt still requires manual add
      })
    }

    return prompts
  }, [activeTab, selectedTemplate, pastedHtml, selections, duration, delay, customPrompt, addedPrompts])

  // Toggle section collapse
  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }, [])

  // Handle option selection
  const handleSelect = useCallback((sectionId: string, optionId: string, multiSelect?: boolean) => {
    setSelections((prev) => {
      if (multiSelect) {
        const current = (prev[sectionId] as string[]) || []
        if (current.includes(optionId)) {
          return { ...prev, [sectionId]: current.filter((id) => id !== optionId) }
        }
        return { ...prev, [sectionId]: [...current, optionId] }
      }
      // Single select - toggle off if same
      if (prev[sectionId] === optionId) {
        const { [sectionId]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [sectionId]: optionId }
    })
  }, [])

  // Clear a section
  const clearSection = useCallback((sectionId: string) => {
    setSelections((prev) => {
      const { [sectionId]: _, ...rest } = prev
      return rest
    })
  }, [])

  // Toggle prompt added state
  const togglePromptAdded = useCallback((promptId: string) => {
    setAddedPrompts((prev) => {
      const next = new Set(prev)
      if (next.has(promptId)) {
        next.delete(promptId)
      } else {
        next.add(promptId)
      }
      return next
    })
  }, [])

  // Add all prompts to chat - all selections are auto-added
  const handleAddToPrompt = useCallback(() => {
    const allPromptTexts = generatedPrompts.filter((p) => p.isAdded).map((p) => p.text)
    if (allPromptTexts.length > 0) {
      onAddToPrompt(allPromptTexts.join(" "))
      onClose()
    }
  }, [generatedPrompts, onAddToPrompt, onClose])

  // Count of added prompts for button state
  const addedCount = generatedPrompts.filter((p) => p.isAdded).length

  // Reset everything
  const handleReset = useCallback(() => {
    setSelections({})
    setDuration(DURATION_SLIDER.defaultValue)
    setDelay(DELAY_SLIDER.defaultValue)
    setAddedPrompts(new Set())
    setCustomPrompt("")
    setSelectedTemplate(null)
    setPastedHtml("")
  }, [])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-[#1a1a1e] w-[95vw] max-w-[1400px] h-[90vh] rounded-xl flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <h2 className="text-white font-semibold text-lg">PROMPT BUILDER</h2>
            <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded font-medium">Pro</span>
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </button>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Options */}
          <div className="w-[580px] border-r border-white/10 flex flex-col overflow-hidden">
            {/* Tabs */}
            <div className="flex gap-2 p-4 pb-3">
              <button
                onClick={() => setActiveTab("template")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "template"
                    ? "bg-blue-600 text-white"
                    : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Search className="w-4 h-4" />
                Adapt from Template
              </button>
              <button
                onClick={() => setActiveTab("html")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "html"
                    ? "bg-blue-600 text-white"
                    : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Code className="w-4 h-4" />
                Adapt from HTML
              </button>
            </div>

            {/* Scrollable Options Area */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {/* Template Gallery or HTML Input */}
              {activeTab === "template" ? (
                <div className="mb-6">
                  <h3 className="text-white font-medium mb-3">Template Gallery</h3>

                  {/* Search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search templates"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-hidden focus:border-blue-500"
                    />
                  </div>

                  {/* Category Pills */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {TEMPLATE_CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedCategory === cat.id
                            ? "bg-blue-600 text-white"
                            : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>

                  {/* Template Grid */}
                  <div className="grid grid-cols-3 gap-3">
                    {MOCK_TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                        className={`group relative bg-white/5 rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedTemplate === template.id
                            ? "border-blue-500"
                            : "border-transparent hover:border-white/20"
                        }`}
                      >
                        {/* Thumbnail placeholder */}
                        <div className="aspect-[4/3] bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                          <span className="text-gray-500 text-xs">{template.name.slice(0, 2).toUpperCase()}</span>
                        </div>

                        {/* Selection checkmark */}
                        {selectedTemplate === template.id && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}

                        {/* Pro badge */}
                        {template.isPro && (
                          <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-white/10 text-white text-xs rounded">
                            PRO
                          </span>
                        )}

                        {/* Info */}
                        <div className="p-2 text-left">
                          <p className="text-white text-xs font-medium truncate">{template.name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-gray-500 text-xs truncate">{template.author}</span>
                            <span className="text-gray-600 text-xs">• {template.views}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <h3 className="text-white font-medium mb-3">Paste HTML Code</h3>
                  <textarea
                    value={pastedHtml}
                    onChange={(e) => setPastedHtml(e.target.value)}
                    placeholder="Paste your HTML code here..."
                    className="w-full h-40 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-hidden focus:border-blue-500 resize-none font-mono"
                  />
                </div>
              )}

              {/* Layout Type */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "layoutType")!}
                selection={selections.layoutType}
                onSelect={(optionId) => handleSelect("layoutType", optionId)}
                isCollapsed={collapsedSections.has("layoutType")}
                onToggle={() => toggleSection("layoutType")}
                deviceType={deviceType}
                onDeviceChange={setDeviceType}
                showDeviceToggle
              />

              {/* Layout Configuration */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "layoutConfig")!}
                selection={selections.layoutConfig}
                onSelect={(optionId) => handleSelect("layoutConfig", optionId)}
                isCollapsed={collapsedSections.has("layoutConfig")}
                onToggle={() => toggleSection("layoutConfig")}
              />

              {/* Style */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "style")!}
                selection={selections.style}
                onSelect={(optionId) => handleSelect("style", optionId)}
                isCollapsed={collapsedSections.has("style")}
                onToggle={() => toggleSection("style")}
              />

              {/* Theme */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "theme")!}
                selection={selections.theme}
                onSelect={(optionId) => handleSelect("theme", optionId)}
                isCollapsed={collapsedSections.has("theme")}
                onToggle={() => toggleSection("theme")}
              />

              {/* Accent Color */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "accentColor")!}
                selection={selections.accentColor}
                onSelect={(optionId) => handleSelect("accentColor", optionId)}
                isCollapsed={collapsedSections.has("accentColor")}
                onToggle={() => toggleSection("accentColor")}
              />

              {/* Background Color */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "backgroundColor")!}
                selection={selections.backgroundColor}
                onSelect={(optionId) => handleSelect("backgroundColor", optionId)}
                isCollapsed={collapsedSections.has("backgroundColor")}
                onToggle={() => toggleSection("backgroundColor")}
              />

              {/* Border Color */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "borderColor")!}
                selection={selections.borderColor}
                onSelect={(optionId) => handleSelect("borderColor", optionId)}
                isCollapsed={collapsedSections.has("borderColor")}
                onToggle={() => toggleSection("borderColor")}
              />

              {/* Shadow */}
              <OptionSection
                section={PROMPT_SECTIONS.find((s) => s.id === "shadow")!}
                selection={selections.shadow}
                onSelect={(optionId) => handleSelect("shadow", optionId)}
                isCollapsed={collapsedSections.has("shadow")}
                onToggle={() => toggleSection("shadow")}
              />

              {/* Typography Section Header */}
              <div className="flex items-center gap-2 my-4 pt-4 border-t border-white/10">
                <button onClick={() => toggleSection("typography")} className="text-gray-400 hover:text-white">
                  {collapsedSections.has("typography") ? (
                    <ChevronRight className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Typography</span>
              </div>

              {!collapsedSections.has("typography") && (
                <>
                  {/* Typeface Family */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "typefaceFamily")!}
                    selection={selections.typefaceFamily}
                    onSelect={(optionId) => handleSelect("typefaceFamily", optionId)}
                    onClear={() => clearSection("typefaceFamily")}
                    isCollapsed={collapsedSections.has("typefaceFamily")}
                    onToggle={() => toggleSection("typefaceFamily")}
                  />

                  {/* Heading Font */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "headingFont")!}
                    selection={selections.headingFont}
                    onSelect={(optionId) => handleSelect("headingFont", optionId)}
                    onClear={() => clearSection("headingFont")}
                    isCollapsed={collapsedSections.has("headingFont")}
                    onToggle={() => toggleSection("headingFont")}
                  />

                  {/* Body Font */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "bodyFont")!}
                    selection={selections.bodyFont}
                    onSelect={(optionId) => handleSelect("bodyFont", optionId)}
                    onClear={() => clearSection("bodyFont")}
                    isCollapsed={collapsedSections.has("bodyFont")}
                    onToggle={() => toggleSection("bodyFont")}
                  />

                  {/* Heading Size */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "headingSize")!}
                    selection={selections.headingSize}
                    onSelect={(optionId) => handleSelect("headingSize", optionId)}
                    onClear={() => clearSection("headingSize")}
                    isCollapsed={collapsedSections.has("headingSize")}
                    onToggle={() => toggleSection("headingSize")}
                  />

                  {/* Heading Letter Spacing */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "headingLetterSpacing")!}
                    selection={selections.headingLetterSpacing}
                    onSelect={(optionId) => handleSelect("headingLetterSpacing", optionId)}
                    onClear={() => clearSection("headingLetterSpacing")}
                    isCollapsed={collapsedSections.has("headingLetterSpacing")}
                    onToggle={() => toggleSection("headingLetterSpacing")}
                  />
                </>
              )}

              {/* Animation Section Header */}
              <div className="flex items-center gap-2 my-4 pt-4 border-t border-white/10">
                <button onClick={() => toggleSection("animation")} className="text-gray-400 hover:text-white">
                  {collapsedSections.has("animation") ? (
                    <ChevronRight className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Animation</span>
              </div>

              {!collapsedSections.has("animation") && (
                <>
                  {/* Animation Type */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "animationType")!}
                    selection={selections.animationType}
                    onSelect={(optionId) => handleSelect("animationType", optionId, true)}
                    onClear={() => clearSection("animationType")}
                    isCollapsed={collapsedSections.has("animationType")}
                    onToggle={() => toggleSection("animationType")}
                    multiSelect
                  />

                  {/* Scene */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "animationScene")!}
                    selection={selections.animationScene}
                    onSelect={(optionId) => handleSelect("animationScene", optionId)}
                    onClear={() => clearSection("animationScene")}
                    isCollapsed={collapsedSections.has("animationScene")}
                    onToggle={() => toggleSection("animationScene")}
                  />

                  {/* Duration Slider */}
                  <SliderOption
                    label="DURATION"
                    value={duration}
                    onChange={setDuration}
                    min={DURATION_SLIDER.min}
                    max={DURATION_SLIDER.max}
                    step={DURATION_SLIDER.step}
                    unit={DURATION_SLIDER.unit}
                  />

                  {/* Delay Slider */}
                  <SliderOption
                    label="DELAY"
                    value={delay}
                    onChange={setDelay}
                    min={DELAY_SLIDER.min}
                    max={DELAY_SLIDER.max}
                    step={DELAY_SLIDER.step}
                    unit={DELAY_SLIDER.unit}
                  />

                  {/* Timing */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "timing")!}
                    selection={selections.timing}
                    onSelect={(optionId) => handleSelect("timing", optionId)}
                    onClear={() => clearSection("timing")}
                    isCollapsed={collapsedSections.has("timing")}
                    onToggle={() => toggleSection("timing")}
                  />

                  {/* Iterations */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "iterations")!}
                    selection={selections.iterations}
                    onSelect={(optionId) => handleSelect("iterations", optionId)}
                    onClear={() => clearSection("iterations")}
                    isCollapsed={collapsedSections.has("iterations")}
                    onToggle={() => toggleSection("iterations")}
                  />

                  {/* Direction */}
                  <OptionSection
                    section={PROMPT_SECTIONS.find((s) => s.id === "direction")!}
                    selection={selections.direction}
                    onSelect={(optionId) => handleSelect("direction", optionId)}
                    onClear={() => clearSection("direction")}
                    isCollapsed={collapsedSections.has("direction")}
                    onToggle={() => toggleSection("direction")}
                  />
                </>
              )}
            </div>
          </div>

          {/* Right Panel - Preview and Generated Prompts */}
          <div className="flex-1 flex flex-col overflow-hidden bg-[#141417]">
            {/* Preview Area */}
            <div className="flex-1 p-4 overflow-hidden">
              <div className="h-full bg-white/5 rounded-lg border border-white/10 overflow-hidden">
                {/* Preview Header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/5">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors">Copy</button>
                    <div className="flex items-center p-0.5 bg-white/5 rounded">
                      <button className="px-2 py-1 text-xs text-white bg-white/10 rounded">Preview</button>
                      <button className="px-2 py-1 text-xs text-gray-400 hover:text-white">Code</button>
                    </div>
                  </div>
                </div>

                {/* Preview Content */}
                <div className="h-full flex items-center justify-center text-gray-500">
                  <p className="text-sm">Preview will appear here</p>
                </div>
              </div>
            </div>

            {/* Generated Prompts */}
            <div className="border-t border-white/10 p-4 max-h-[45%] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm">GENERATED PROMPTS</h3>
                <Button
                  onClick={handleAddToPrompt}
                  disabled={addedCount === 0}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add to Prompt {addedCount > 0 && `(${addedCount})`}
                </Button>
              </div>

              {/* Custom Prompt Input */}
              <div className="mb-3">
                <div
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    addedPrompts.has("custom") ? "bg-blue-600/20 border-blue-500" : "bg-white/5 border-white/10"
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div
                      className={`w-2 h-2 rounded-full ${addedPrompts.has("custom") ? "bg-blue-500" : "bg-gray-600"}`}
                    />
                    <input
                      type="text"
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      placeholder="Add custom prompt..."
                      className="flex-1 bg-transparent text-white text-sm placeholder-gray-500 focus:outline-hidden"
                    />
                  </div>
                  <button
                    onClick={() => customPrompt.trim() && togglePromptAdded("custom")}
                    className={`text-sm ${
                      addedPrompts.has("custom")
                        ? "text-red-400 hover:text-red-300"
                        : "text-blue-400 hover:text-blue-300"
                    }`}
                  >
                    {addedPrompts.has("custom") ? "Remove" : "Add"}
                  </button>
                </div>
              </div>

              {/* Generated Prompt List - auto-added selections */}
              <div className="space-y-2">
                {generatedPrompts
                  .filter((p) => p.category !== "custom")
                  .map((prompt) => (
                    <div
                      key={prompt.id}
                      className="flex items-center p-3 rounded-lg border bg-blue-600/20 border-blue-500"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-blue-500" />
                        <span className="text-white text-sm">{prompt.text}</span>
                      </div>
                    </div>
                  ))}
              </div>

              {generatedPrompts.length === 0 && !customPrompt && (
                <p className="text-gray-500 text-sm text-center py-4">Select options to generate prompts</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface OptionSectionProps {
  section: {
    id: string
    label: string
    options: PromptOption[]
    clearable?: boolean
  }
  selection: string | string[] | undefined
  onSelect: (optionId: string) => void
  onClear?: () => void
  isCollapsed: boolean
  onToggle: () => void
  multiSelect?: boolean
  showDeviceToggle?: boolean
  deviceType?: string
  onDeviceChange?: (type: string) => void
}

const OptionSection = ({
  section,
  selection,
  onSelect,
  onClear,
  isCollapsed,
  onToggle,
  multiSelect,
  showDeviceToggle,
  deviceType,
  onDeviceChange,
}: OptionSectionProps) => {
  const isSelected = (optionId: string) => {
    if (!selection) return false
    if (Array.isArray(selection)) return selection.includes(optionId)
    return selection === optionId
  }

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button onClick={onToggle} className="text-gray-400 hover:text-white">
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">{section.label}</span>
          {section.clearable && selection && (
            <button onClick={onClear} className="text-gray-500 hover:text-gray-400 ml-1">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        {showDeviceToggle && deviceType && onDeviceChange && (
          <div className="flex items-center p-0.5 bg-white/5 rounded">
            {DEVICE_TYPES.map((device) => (
              <button
                key={device.id}
                onClick={() => onDeviceChange(device.id)}
                className={`px-2 py-1 text-xs rounded ${
                  deviceType === device.id ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {device.label}
              </button>
            ))}
          </div>
        )}
        {multiSelect && <span className="text-xs text-gray-500">Select Multiple</span>}
      </div>

      {!isCollapsed && (
        <div className="grid grid-cols-6 gap-2">
          {section.options.map((option) => (
            <button
              key={option.id}
              onClick={() => onSelect(option.id)}
              className={`flex flex-col items-center justify-center p-2 rounded-lg border text-center transition-colors min-h-[60px] ${
                isSelected(option.id)
                  ? "bg-blue-600/20 border-blue-500 text-blue-400"
                  : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white hover:border-white/20"
              }`}
            >
              {/* Option icon/preview placeholder */}
              <div className="w-full h-6 mb-1 flex items-center justify-center">
                <div className="w-10 h-2 bg-current rounded opacity-40" />
              </div>
              <span className="text-xs truncate w-full">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface SliderOptionProps {
  label: string
  value: number
  onChange: (value: number) => void
  min: number
  max: number
  step: number
  unit: string
}

const SliderOption = ({ label, value, onChange, min, max, step, unit }: SliderOptionProps) => {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">{label}</span>
        <span className="text-xs text-gray-300">
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer slider-thumb"
      />
    </div>
  )
}

export default PromptBuilderModal
